import React, { useState, useEffect, useCallback } from "react";
import { Modal, Input, Button, Spin, Select, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  fetchCxGptProviders,
  fetchCxGptProviderModels,
  saveCxGptProvider,
  saveCxGptProviderSelection,
} from "util/copilotChatApi";
import styles from "./CxGptProviderKeyModal.module.scss";

/**
 * INFO: Integrations modal — connect OpenAI or alternative LLM for Pivony Advisor (user-facing copy avoids “BYOK”).
 */
const CxGptProviderKeyModal = ({
  open,
  provider,
  theme,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [hasStoredKey, setHasStoredKey] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(undefined);
  const [fetchingModels, setFetchingModels] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await fetchCxGptProviders();
      if (s && s.cancel) {
        message.error(t("cx_gpt.network_error"));
        return;
      }
      if (s && s.error) {
        message.error(String(s.error));
        return;
      }
      if (s && !s.error && Array.isArray(s.providers)) {
        const row = s.providers.find((x) => x.provider === provider);
        setHasStoredKey(Boolean(row && row.has_api_key));
        setSelectedModel(row?.selected_model || undefined);
        setApiKey("");
      }
    } finally {
      setLoading(false);
    }
  }, [provider, t]);

  useEffect(() => {
    if (open && provider) {
      load();
    }
  }, [open, provider, load]);

  const handleSave = async () => {
    if (!apiKey.trim() && !hasStoredKey) {
      message.warning(t("cx_gpt.api_key"));
      return;
    }
    setSaving(true);
    try {
      if (apiKey.trim()) {
        const res = await saveCxGptProvider(provider, { api_key: apiKey.trim() });
        if (res && res.cancel) {
          message.error(t("cx_gpt.network_error"));
          return;
        }
        if (res && res.error) {
          message.error(String(res.error));
          return;
        }
      }
      if (selectedModel && String(selectedModel).trim()) {
        await saveCxGptProviderSelection(provider, String(selectedModel).trim());
      }
      message.success(t("cx_gpt.settings_saved"));
      if (typeof onSaved === "function") onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const fetchModels = async () => {
    setFetchingModels(true);
    try {
      const res = await fetchCxGptProviderModels(provider);
      if (!res || res.cancel) {
        message.error(t("cx_gpt.network_error"));
        return;
      }
      if (res.error) {
        message.error(String(res.error));
        return;
      }
      const list = Array.isArray(res.models) ? res.models : [];
      setModels(list);
      if (!selectedModel && res.default_model) {
        setSelectedModel(res.default_model);
      }
    } finally {
      setFetchingModels(false);
    }
  };

  const modalBodyStyle = {
    padding: "28px 32px",
    borderRadius: "15px",
    background: theme === "dark" ? "#1C193B" : "#ffffff",
    overflow: "hidden",
  };

  const title =
    provider === "openai"
      ? t("settings.228")
      : provider === "deepseek"
        ? t("settings.229")
        : t("cx_gpt.settings_title");

  return (
    <Modal
      open={open && Boolean(provider)}
      title={title}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      bodyStyle={modalBodyStyle}
      style={{ overflow: "hidden", borderRadius: "15px", padding: 0 }}
    >
      {loading ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : (
        <>
          <div
            className={`${styles.field} ${
              theme === "light" || theme === "brand" ? styles.lightField : ""
            }`}
          >
            <label className={styles.label} htmlFor={`cxgpt-key-${provider}`}>
              {t("cx_gpt.api_key")}
              {hasStoredKey ? (
                <span className={styles.keyBadge}>{t("cx_gpt.key_stored")}</span>
              ) : null}
            </label>
            <Input.Password
              id={`cxgpt-key-${provider}`}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t("cx_gpt.api_key_placeholder")}
              disabled={saving}
            />
            <div className={styles.hint}>{t("cx_gpt.api_key_hint")}</div>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>{t("cx_gpt.model_for_chat")}</div>
            <div className={styles.modelRow}>
              <Button onClick={fetchModels} loading={fetchingModels} disabled={saving}>
                {t("settings.234")}
              </Button>
              <Select
                value={selectedModel}
                onChange={setSelectedModel}
                className={styles.modelSelect}
                disabled={saving || fetchingModels || !models.length}
                options={models.map((m) => ({ value: m.id, label: m.label || m.id }))}
                placeholder={t("settings.235")}
              />
            </div>
          </div>
          <div className={styles.footer}>
            <Button onClick={onClose} disabled={saving}>
              {t("cx_gpt.clear_key_cancel")}
            </Button>
            <Button type="primary" onClick={handleSave} loading={saving}>
              {t("settings.230")}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CxGptProviderKeyModal;
